import { useCallback, useEffect, useRef, useState } from 'react';
import logError from '../../utils/logError';
import { createToken } from './adaRequests';

const expirationBufferInMs = 60 * 5 * 1000; // 5 minutes
const defaultIntervalInMs = 60 * 1000; // 1 minute
const maxRetries = 3;

type ParsedToken = {
  token: string;
  expiresAt: number;
};

/**
 * Validates and parses a JWT token, extracting the expiration time.
 * @throws {Error} if token is invalid or missing required fields
 */
function validateAndParseToken(token: string | null | undefined): ParsedToken {
  if (!token) {
    throw new Error('Token not found in response');
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT token format');
  }

  const encodedPayload = parts[1];
  const parsedPayload = JSON.parse(atob(encodedPayload));

  if (!parsedPayload.exp || typeof parsedPayload.exp !== 'number') {
    throw new Error('Token missing valid expiration');
  }

  return {
    token,
    expiresAt: parsedPayload.exp * 1000
  };
}

export default function useAdaToken() {
  const [isActive, setIsActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const isRequestInFlightRef = useRef(false);

  const scheduleRefresh = useCallback((delay: number) => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    timerRef.current = setTimeout(async () => {
      // Prevent duplicate requests if one is already in-flight
      if (isRequestInFlightRef.current) {
        return;
      }

      isRequestInFlightRef.current = true;
      try {
        const response = await createToken();
        const { token, expiresAt } = validateAndParseToken(response?.token);

        // Success - reset retry count
        retryCountRef.current = 0;

        // Calculate next refresh time
        const msUntilExpiration = expiresAt - Date.now();
        const nextDelay = Math.max(defaultIntervalInMs, msUntilExpiration - expirationBufferInMs);

        // Update Ada with new token
        if (window.adaEmbed?.setSensitiveMetaFields) {
          window.adaEmbed.setSensitiveMetaFields({
            alltrailsUserToken: token
          });
        }

        // Schedule next refresh
        scheduleRefresh(nextDelay);
      } catch (error) {
        const currentRetries = retryCountRef.current;

        logError(new Error('Ada token refresh failed', { cause: error }), event => {
          event.addMetadata('ada', {
            retryCount: currentRetries,
            action: 'token_refresh_failed'
          });
        });

        // Check if we've exceeded max retries
        if (currentRetries >= maxRetries) {
          logError(new Error('Ada token refresh retries reached', { cause: error }), event => {
            event.addMetadata('ada', {
              retryCount: currentRetries,
              action: 'token_refresh_retries_exceeded'
            });
          });

          // Stop the refresh loop
          setIsActive(false);
          return;
        }

        // Increment retry count and schedule retry
        retryCountRef.current += 1;
        scheduleRefresh(defaultIntervalInMs);
      } finally {
        isRequestInFlightRef.current = false;
      }
    }, delay);
  }, []);

  // Effect for managing the refresh loop
  useEffect(() => {
    if (isActive) {
      // Start immediately
      scheduleRefresh(0);
    }

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isActive, scheduleRefresh]);

  // Public API
  const resetAdaTokenHandler = useCallback(() => {
    retryCountRef.current = 0;
    // Clear existing timer before scheduling new one
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // If already active, we need to manually trigger since effect won't re-run
    if (isActive) {
      scheduleRefresh(0);
    } else {
      setIsActive(true);
    }
  }, [isActive, scheduleRefresh]);

  return { resetAdaTokenHandler };
}
